import React from 'react';
import { Lock, Search, UserPlus } from 'lucide-react';
import { CHECK_IN_COLUMNS, isWalkIn } from '../../../lib/eventJourney';
import { Switch } from '../../ui/switch';
import { cardSub } from './guestUi';

const stateText = (card, key) => {
    if (key === 'attended') return isWalkIn(card) ? 'Walk-in · checked in' : `Checked in${card.attendance_method === 'face_match' ? ' · Face match' : ''}`;
    if (key === 'no_show') return 'No-show · switch on if they came late';
    return 'Going · not checked in';
};

/**
 * The event-day view of the same rows: one row per Going or Attended guest (and, once the event has ended,
 * No-show) with a switch instead of a button (OWN-10). Switch on checks in, off undoes; after the event a
 * No-show switched on is corrected to Attended. The switches are disabled until the event starts.
 */
export default function GuestsCheckIn({
    cards, columnOf, phase, summary, search, onSearch, pendingIds, readOnly, lockText, onToggle, onAddWalkIn,
}) {
    const locked = phase === 'before';
    const rows = cards.filter((card) => CHECK_IN_COLUMNS.includes(columnOf(card)))
        .sort((a, b) => (columnOf(a) === 'attended' ? 1 : 0) - (columnOf(b) === 'attended' ? 1 : 0) || a.name.localeCompare(b.name));
    const attended = summary?.attended ?? cards.filter((card) => columnOf(card) === 'attended').length;
    // Going plus walk-ins: the rows listed below, and the set `attended` counts from (#1013 QA).
    const total = summary?.checkin_total ?? summary?.going ?? cards.filter((card) => ['going', 'attended'].includes(columnOf(card))).length;
    const walkIns = summary?.walk_ins ?? cards.filter((card) => isWalkIn(card) && columnOf(card) === 'attended').length;
    return (
        <div data-testid="guests-checkin" className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <label className="relative min-w-[240px] flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                    <input aria-label="Find a guest by name or company" placeholder="Find a guest by name or company" value={search} onChange={(event) => onSearch(event.target.value)}
                        className="h-11 w-full rounded-xl border border-stone-200 bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                </label>
                <div className="text-right" data-testid="guests-checkin-count">
                    <span className="block text-xl font-semibold tabular-nums text-stone-900">{attended} / {total}</span>
                    <span className="block text-xs text-stone-500">checked in · {walkIns} walk-in{walkIns === 1 ? '' : 's'}</span>
                </div>
                <button type="button" data-testid="guests-add-walk-in" disabled={locked || readOnly} onClick={onAddWalkIn}
                    title={locked ? 'Start the event to add walk-ins' : undefined}
                    className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-stone-900 px-3.5 text-sm font-semibold text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50">
                    <UserPlus className="h-4 w-4" aria-hidden="true" />Add walk-in
                </button>
            </div>
            {locked && (
                <p data-testid="guests-checkin-locked" className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm text-stone-600">
                    <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />{lockText}
                </p>
            )}
            <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
                {rows.map((card) => {
                    const key = columnOf(card);
                    const on = key === 'attended';
                    const noShow = key === 'no_show';
                    const tip = locked ? 'Start the event to open check-in' : on ? 'Switch off to undo the check-in' : 'Switch on to check in';
                    return (
                        <div key={card.contact_id} data-testid={`checkin-row-${card.contact_id}`}
                            className={`flex items-center gap-3 rounded-xl border p-3.5 ${on ? 'border-teal-100 bg-teal-50' : 'border-stone-200 bg-white'}`}>
                            <div className="min-w-0 flex-1">
                                <span className="block truncate text-[15px] font-semibold text-stone-900">{card.name}</span>
                                <span className="block truncate text-[12.5px] text-stone-500">{cardSub(card)}</span>
                                <span className={`mt-1 block text-xs font-medium ${on ? 'text-teal-700' : noShow ? 'text-red-700' : 'text-stone-500'}`}>{stateText(card, key)}</span>
                            </div>
                            <div className="flex flex-col items-center gap-1" title={tip}>
                                <Switch size="lg" aria-label={card.name} checked={on} disabled={locked || readOnly || pendingIds.includes(card.contact_id)}
                                    onCheckedChange={(checked) => onToggle(card, checked)} data-testid={`checkin-switch-${card.contact_id}`} />
                                <span className={`text-[11px] font-semibold ${on ? 'text-teal-700' : 'text-stone-500'}`}>{on ? 'Checked in' : 'Not in'}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            {rows.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 px-4 py-8 text-center text-sm text-stone-500">No Going guests match. Guests who RSVP yes, or who check in, appear here.</p>}
        </div>
    );
}
